import { observable, runInAction } from 'mobx';

import ApiService from '@app/Services/ApiService';
import { OptionsModel } from '@app/Models/Models';

export type RemoteOption = OptionsModel & { hint?: string; status?: string };

// Mirrors EDW.DataSync.Model.Api.v1.JobParameterOptionsModel, which always answers HTTP 200 and
// reports upstream failures in loadError so that a broken option source cannot block the dialog.
export type RemoteOptionsResponse = {
    options?: RemoteOption[];
    loadError?: string;
    isTruncated?: boolean;
    searchMinLength?: number;
};

const ABSOLUTE_URL_PATTERN = /^[a-z][a-z0-9+.-]*:/i;

const normalize = (value?: string) => (value || '').trim().toLowerCase();

/**
 * Options of one endpoint. Endpoints come in two flavours, distinguished by searchMinLength in the
 * response: a complete list that is filtered locally (forms, macros), or a searchable source that
 * can only answer queries (employees). Both are served through the same calls; the only difference
 * is that a searchable source is re-queried while the user types.
 */
export class RemoteOptionsEntry {
    @observable public options: RemoteOption[] = [];
    @observable public loadError: string = '';
    @observable public isTruncated: boolean = false;
    @observable public searchMinLength: number = 0;
    @observable public isLoaded: boolean = false;
    @observable public isSearching: boolean = false;

    private readonly _url: string;
    private _request: Promise<void> | null = null;
    private _resolvedValues: Set<string> = new Set<string>();
    private _lastSearch: string | null = null;
    private _pendingRequestId: number = 0;

    constructor(url: string) {
        this._url = url;
    }

    public get isSearchable(): boolean {
        return this.searchMinLength > 0;
    }

    /** First load for a parameter. Also asks the endpoint to resolve the value it currently holds. */
    public load(value?: string): Promise<void> {
        if (!this._request) {
            this._request = this._fetch({ value });
        } else if (value && !this._resolvedValues.has(normalize(value))) {
            // Another parameter shares this endpoint but holds a different key: a searchable source
            // only knows the keys it was asked about, so resolve this one too.
            this._request = this._request.then(() => {
                if (this.isSearchable) return this._fetch({ value, keepOptions: true });
                return undefined;
            });
        }
        return this._request;
    }

    public reload(value?: string): Promise<void> {
        this._request = null;
        this._resolvedValues.clear();
        this._lastSearch = null;
        runInAction(() => {
            this.isLoaded = false;
            this.loadError = '';
        });
        return this.load(value);
    }

    /** Queries a searchable endpoint. No-op for endpoints that already returned the complete list. */
    public search(term: string, value?: string): Promise<void> {
        if (!this.isSearchable) return Promise.resolve();

        const trimmed = term.trim();
        if (trimmed.length < this.searchMinLength || trimmed === this._lastSearch) return Promise.resolve();

        this._lastSearch = trimmed;
        return this._fetch({ search: trimmed, value, keepOptions: true });
    }

    public findOption(value: string): RemoteOption | undefined {
        const key = normalize(value);
        if (!key) return undefined;

        return this.options.find(option => normalize(option.value) === key);
    }

    /**
     * True when the endpoint was asked about this exact key and did not return it, which is the only
     * case where "unknown key" is an honest statement. A key that was never looked up, or one that a
     * search simply did not match, stays unjudged.
     */
    public isKnownMissing(value: string): boolean {
        const key = normalize(value);
        if (!key || !this.isLoaded || !!this.loadError) return false;

        return this._resolvedValues.has(key) && !this.findOption(value);
    }

    private async _fetch(request: { search?: string; value?: string; keepOptions?: boolean }): Promise<void> {
        // ApiService prefixes location.origin and /api, so an absolute url would be mangled.
        if (ABSOLUTE_URL_PATTERN.test(this._url)) {
            runInAction(() => {
                this.options = [];
                this.loadError = `Unsupported options url '${this._url}': it must be app relative and start with /api.`;
                this.isLoaded = true;
            });
            return;
        }

        const requestId = ++this._pendingRequestId;
        runInAction(() => { this.isSearching = true; });

        try {
            const { data } = await ApiService.get<RemoteOptionsResponse>(this._url, {
                search: request.search || undefined,
                value: request.value || undefined
            });

            // A slower earlier keystroke must not overwrite the results of a later one.
            if (requestId !== this._pendingRequestId) return;

            runInAction(() => {
                this.options = request.keepOptions
                    ? this._mergeOptions(data?.options ?? [])
                    : (data?.options ?? []);
                this.loadError = data?.loadError ?? '';
                this.isTruncated = !!data?.isTruncated;
                this.searchMinLength = data?.searchMinLength ?? 0;
                this.isLoaded = true;
                this.isSearching = false;
            });

            if (request.value) {
                this._resolvedValues.add(normalize(request.value));
            }
        } catch {
            if (requestId !== this._pendingRequestId) return;

            runInAction(() => {
                if (!request.keepOptions) this.options = [];
                this.loadError = 'Option list could not be loaded. You can still type and save a value.';
                this.isLoaded = true;
                this.isSearching = false;
            });
        }
    }

    private _mergeOptions(incoming: RemoteOption[]): RemoteOption[] {
        // Keeps already resolved keys (a saved value the user has not searched for) alongside new hits.
        const merged = [...incoming];
        const incomingKeys = new Set(incoming.map(option => normalize(option.value)));

        this.options.forEach(option => {
            if (this._resolvedValues.has(normalize(option.value)) && !incomingKeys.has(normalize(option.value))) {
                merged.push(option);
            }
        });

        return merged;
    }
}

export class RemoteOptionsStore {
    private static _entries: Map<string, RemoteOptionsEntry> = new Map<string, RemoteOptionsEntry>();

    public static get(url: string): RemoteOptionsEntry {
        let entry = RemoteOptionsStore._entries.get(url);
        if (!entry) {
            entry = new RemoteOptionsEntry(url);
            RemoteOptionsStore._entries.set(url, entry);
        }
        return entry;
    }
}
